import { ExternalLink, PenLine } from 'lucide-react';
import { Button } from '@dreamtree-org/twreact-ui';
import SectionHeading from '@/components/site/SectionHeading';
import Reveal from '@/components/ui/Reveal';
import { GoogleG, Stars } from '@/components/google/GoogleMarks';
import { useSettings } from '@site/content';
import { useGoogleReviews } from '@api/public';

/**
 * Live 4★+ Google reviews (max 5 — a Places API limit). Renders nothing until
 * the server has a key and returns reviews. Review text is shown unmodified with
 * author attribution, per Google Maps Platform policy.
 */
export default function GoogleReviews({ className = 'bg-ink-50' }) {
  const { google = {} } = useSettings();
  const g = useGoogleReviews(google);
  if (!g.live || !g.reviews.length) return null;

  return (
    <section className={`py-16 sm:py-24 ${className}`} aria-label="Google reviews">
      <div className="site-container">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionHeading eyebrow={google.sectionEyebrow || 'Google reviews'} title={google.sectionTitle || 'What our customers say on Google'} />
            <div className="mt-5 flex items-center gap-3">
              <GoogleG className="h-8 w-8" />
              <span className="text-3xl font-bold text-ink-950">{Number(g.rating).toFixed(1)}</span>
              <span>
                <Stars value={Number(g.rating)} className="h-5 w-5" />
                <span className="block text-sm text-ink-500">Based on {g.count} Google reviews</span>
              </span>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Button as="a" href={g.mapsUrl} target="_blank" rel="noreferrer" variant="outline" rightIcon={<ExternalLink className="h-4 w-4" />}>
              See all reviews
            </Button>
            {g.writeReviewUrl ? (
              <Button as="a" href={g.writeReviewUrl} target="_blank" rel="noreferrer" leftIcon={<PenLine className="h-4 w-4" />}>
                Write a review
              </Button>
            ) : null}
          </div>
        </div>

        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {g.reviews.map((r, i) => (
            <li key={`${r.author}-${i}`}>
            <Reveal delay={i * 0.06} className="h-full">
              <figure className="flex h-full flex-col rounded-3xl border border-[var(--app-border)] bg-white p-6 shadow-sm">
                <figcaption className="flex items-center gap-3">
                  {r.photo ? (
                    <img src={r.photo} alt="" referrerPolicy="no-referrer" loading="lazy" className="h-11 w-11 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-flame-x font-bold text-white">{r.author.charAt(0)}</span>
                  )}
                  <span className="min-w-0 flex-1">
                    {r.authorUrl ? (
                      <a href={r.authorUrl} target="_blank" rel="noreferrer" className="block truncate font-semibold text-ink-950 hover:underline">
                        {r.author}
                      </a>
                    ) : (
                      <span className="block truncate font-semibold text-ink-950">{r.author}</span>
                    )}
                    <span className="block text-xs text-ink-500">{r.relativeTime}</span>
                  </span>
                  <GoogleG className="h-5 w-5 shrink-0" />
                </figcaption>
                <Stars value={r.rating} className="mt-4 h-4 w-4" />
                <blockquote className="mt-3 line-clamp-6 flex-1 whitespace-pre-line text-pretty leading-relaxed text-ink-700">{r.text}</blockquote>
                <p className="mt-4 text-xs text-ink-400">Posted on Google</p>
              </figure>
            </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
